import type { CartItem, CartResponse, CartShippingAddress } from "@/features/cart/types/cart.type";
import type { DealCart, DealCartItem, DealCartShippingAddress } from "@/features/deals/types/dealCart.type";
import type { RentalBooking } from "@/features/rentals/types/rentals.type";
import { getItemImageUrl } from "@/features/rentals/utils/itemImage.util";
import type {
  UnifiedBoutiqueCartItem,
  UnifiedCart,
  UnifiedDealsCartItem,
  UnifiedRentalsCartItem,
  UnifiedShippingAddress,
} from "@/shared/types/unified-cart.type";

/**
 * Get display name for a boutique cart item based on its type
 */
const getBoutiqueItemName = (item: CartItem): string => {
  switch (item.item_type) {
    case "article":
      return item.name;
    case "offer":
      return item.label ?? "Offre";
    case "rate":
      return item.event.label;
    case "benefits":
      return item.label;
    default:
      return "Article";
  }
};

/**
 * Get subtitle/description for a boutique cart item
 */
const getBoutiqueItemSubtitle = (item: CartItem): string | undefined => {
  switch (item.item_type) {
    case "article":
      return item.sousgenre.name;
    case "offer":
      return item.date ?? undefined;
    case "rate":
      return `${item.seat_category_name} - ${item.client_type_name}`;
    case "benefits":
      return item.variant_label ?? undefined;
    default:
      return undefined;
  }
};

/**
 * Get type_billet for a boutique cart item, with "Billet Standard" -> "Envoi postal" override
 */
const getBoutiqueItemTypeBillet = (item: CartItem): string | undefined => {
  if (item.item_type !== "article") return undefined;
  const typeBillet = item.type_billet;
  if (!typeBillet) return undefined;
  return typeBillet === "Billet Standard" ? "Envoi postal" : typeBillet;
};

/**
 * Get image URL for a boutique cart item
 */
const getBoutiqueItemImage = (item: CartItem): string | null => {
  switch (item.item_type) {
    case "article":
      return item.sousgenre.image_url;
    case "offer":
      return item.image_url;
    case "rate":
      return item.event.image_url;
    case "benefits":
      return item.image_url;
    default:
      return null;
  }
};

/**
 * Transform a single boutique cart item to unified format
 */
export const transformBoutiqueItem = (item: CartItem): UnifiedBoutiqueCartItem => ({
  id: String(item.id),
  cartType: "boutique",
  quantity: item.quantity,
  price_amount: item.price_amount,
  total_amount: item.total_amount,
  name: getBoutiqueItemName(item),
  image_url: getBoutiqueItemImage(item),
  subtitle: getBoutiqueItemSubtitle(item),
  discount_amount: item.discount_amount,
  original_price_amount: item.original_price_amount,
  originalItem: item,
  item_type: item.item_type,
  minimum_quantity: item.item_type === "article" ? item.minimum_quantity : undefined,
  type_billet: getBoutiqueItemTypeBillet(item),
  multiple_quantity: item.item_type === "article" ? item.multiple_quantity : undefined,
  maximum_quantity: item.maximum_quantity ?? null,
  seat_details: item.item_type === "rate" ? item.seat_details : undefined,
  seat_selection_required: item.item_type === "rate" ? item.seat_selection_required : undefined,
});

/**
 * Transform a single deals cart item to unified format
 */
export const transformDealsItem = (
  item: DealCartItem,
  cartRef?: string,
): UnifiedDealsCartItem => {
  const publicPrice = parseFloat(item.reference?.public_price) || 0;
  const resaleValue = parseFloat(item.reference?.resale_value) || 0;
  const unitDiscount = Math.max(publicPrice - resaleValue, 0);
  const imageUrl = item.family.thumbnail?.large_url ?? item.family.thumbnail?.url ?? null;

  return {
    id: item.id,
    cartType: "deals",
    quantity: item.quantity,
    price_amount: resaleValue,
    total_amount: item.amount,
    name: item.reference?.name ?? "",
    image_url: imageUrl,
    subtitle: item.family?.name ?? "",
    discount_amount: unitDiscount,
    original_price_amount: publicPrice,
    originalItem: item,
    reference_id: item.reference?.id,
    family_id: item.family?.id,
    family_name: item.family.name,
    ref: cartRef,
  };
};

/**
 * Transform boutique shipping address to unified format
 */
export const transformBoutiqueAddress = (
  address: CartShippingAddress,
): UnifiedShippingAddress => ({
  first_name: address.first_name,
  last_name: address.last_name,
  address_line1: address.address_line1,
  address_line2: address.address_line2,
  zip_code: address.zip_code,
  city: address.city,
});

/**
 * Transform deals shipping address to unified format
 */
export const transformDealsAddress = (
  address: DealCartShippingAddress,
): UnifiedShippingAddress => ({
  name: address.name,
  address_line1: address.address_line1,
  address_line2: address.address_line2,
  zip_code: address.zip_code,
  city: address.city,
});

/**
 * Transform a complete boutique cart response to unified format
 */
export const transformBoutiqueCart = (
  cart: CartResponse | undefined,
): UnifiedCart | undefined => {
  if (!cart) {
    return undefined;
  }

  return {
    id: String(cart.id),
    cartType: "boutique",
    items: cart.items.map(transformBoutiqueItem),
    total_items: cart.total_items,
    total_amount: cart.total_amount,
    errors: cart.errors,
    shipping_address: cart.shipping_address
      ? transformBoutiqueAddress(cart.shipping_address)
      : null,
    ttl: cart.ttl,
    reserved_until: cart.reserved_until,
    reservation_expires_at: cart.reservation_expires_at,
  };
};

/**
 * Format a rental booking's date range as `dd/mm/yyyy → dd/mm/yyyy`.
 * Used as the cart-item subtitle so the user spots the period at a
 * glance without leaving the cart page.
 */
const formatRentalDateRange = (start: string | null, end: string | null): string | undefined => {
  if (!start || !end) return undefined;
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };
  return `${fmt(start)} → ${fmt(end)}`;
};

/**
 * Transform a single rental booking (status = "cart") into the
 * `UnifiedCartItem` shape so the cart page can render it with the
 * exact same `UnifiedCartItemCard` used for boutique/deals.
 */
export const transformRentalsItem = (
  booking: RentalBooking,
): UnifiedRentalsCartItem => {
  const rentalAmount = (booking.total_price_cents ?? 0) / 100;
  const depositAmount = (booking.deposit_amount_cents ?? 0) / 100;
  const unitAmount = (booking.unit_price_cents ?? booking.total_price_cents ?? 0) / 100;
  // `total_amount` = loyer uniquement. Depuis la bascule « caution
  // hors compta », la caution est un chèque physique remis au CSE qui
  // n'entre dans aucun calcul Paybox / total panier — exposée
  // séparément via `deposit_amount` pour que la card / le récap
  // puissent l'afficher comme info (mention chèque).
  const totalAmount = rentalAmount;
  // Voyage : une demande à plusieurs tarifs n'a pas de prix unitaire, et sa
  // quantité compte des places, pas des exemplaires. Une seule ligne au total
  // de la demande, les places en sous-titre.
  const isVoyage = booking.item?.kind === "voyage";
  return {
    id: booking.id,
    cartType: "rentals",
    quantity: isVoyage ? 1 : booking.quantity || 1,
    price_amount: isVoyage ? totalAmount : unitAmount,
    total_amount: totalAmount,
    name: booking.item?.name ?? "Location",
    // Ordre `url` → `large_url` → `medium_url` → image générée, aligné
    // sur `RentalCard` (qui s'affiche correctement). On privilégie l'URL
    // du blob de base (`url`, toujours disponible) avant les variants
    // ActiveStorage (`medium_url`/`large_url`) qui, générés
    // paresseusement, peuvent renvoyer un 404 tant qu'ils n'ont pas été
    // matérialisés sur S3 — cause de l'image manquante pour les items
    // médiathèque dans le panier (l'ancien ordre tentait `medium_url`
    // d'abord). Le fallback final génère une image à partir du nom/kind.
    image_url: booking.item?.images[0]?.url
      ?? booking.item?.images[0]?.large_url
      ?? booking.item?.images[0]?.medium_url
      ?? getItemImageUrl(booking.item?.name ?? "Location", booking.item?.kind),
    // La variante distingue deux lignes du même article (essieu double
    // et simple, tarif A et B).
    subtitle: [
      booking.variant_name,
      formatRentalDateRange(booking.start_date, booking.end_date),
      isVoyage ? `${booking.quantity} place${booking.quantity > 1 ? "s" : ""}` : undefined,
    ]
      .filter(Boolean)
      .join(" • "),
    discount_amount: 0,
    original_price_amount: isVoyage ? totalAmount : unitAmount,
    originalItem: booking,
    ref: booking.reference,
    deposit_amount: depositAmount,
  };
};

/**
 * Transform the cart-status subset of `useMyRentalBookings()` into a
 * `UnifiedCart` so it plugs into the same render pipeline as
 * boutique/deals (UnifiedCartItemsList + CartSummary).
 */
export const transformRentalsCart = (
  bookings: RentalBooking[],
): UnifiedCart => {
  const items = bookings.map(transformRentalsItem);
  const totalAmount = items.reduce((sum, it) => sum + it.total_amount, 0);
  return {
    id: "rentals-cart",
    cartType: "rentals",
    items,
    total_items: items.length,
    total_amount: totalAmount,
    shipping_address: null,
  };
};

/**
 * Transform a complete deals cart to unified format
 */
export const transformDealsCart = (
  cart: DealCart | undefined,
): UnifiedCart | undefined => {
  if (!cart) {
    return undefined;
  }

  return {
    id: cart.id,
    cartType: "deals",
    items: cart.items.map((item) => transformDealsItem(item, cart.ref)),
    total_items: cart.total_items,
    total_amount: cart.total_amount,
    errors: cart.errors,
    shipping_address: cart.shipping_address
      ? transformDealsAddress(cart.shipping_address)
      : null,
    ref: cart.ref,
    status: cart.status,
    status_name: cart.status_name,
    delivery_mode: cart.delivery_mode,
  };
};

/**
 * Get display name for unified shipping address
 */
export const getUnifiedAddressDisplayName = (
  address: UnifiedShippingAddress,
): string => {
  if (address.name) {
    return address.name;
  }
  if (address.first_name && address.last_name) {
    return `${address.first_name} ${address.last_name}`;
  }
  return address.first_name ?? address.last_name ?? "";
};

/**
 * Format unified address as single line
 */
export const formatUnifiedAddressLine = (
  address: UnifiedShippingAddress,
): string => {
  const parts = [
    address.address_line1,
    address.address_line2,
    `${address.zip_code} ${address.city}`,
  ].filter(Boolean);

  return parts.join(", ");
};
